import {FormService} from "./form.service";
import {BehaviorSubject, forkJoin, Observable, Subject} from "rxjs";
import {map, tap, timeInterval} from "rxjs/operators";
import {Product} from "../../store/state/order.state";
import {environment} from "../../../environments/environment";

export abstract class CompositeFormService {

	private submitClicked: Subject<boolean> = new BehaviorSubject(false);
	private readonly debug: boolean;

	protected constructor(protected allFormServices: FormService[]) {
		this.debug = environment.showFormServiceLogs;
	}

	submit(submitAction: () => void, onValidationsFailedAction?: () => void, additionalSubmitCheck?: () => boolean, finalizeAction?: () => void): void {
		this.debug && console.info("COMPOSITE FORM SERVICE: submit clicked. State of all form services: ", this.allFormServices);
		this.submitClicked.next(true);
		this.requestSubmitInAllFormServices().pipe(map((formValid: boolean[]) => formValid.every(valid => valid)))
			.subscribe(allFormsValid => {
				if (allFormsValid && (!!additionalSubmitCheck ? additionalSubmitCheck() : true)) {
					this.debug && console.info("COMPOSITE FORM SERVICE: all forms are valid, blocking inputs & invoking submit action", this);
					this.blockInputs();
					submitAction();
				} else {
					this.debug && console.info("COMPOSITE FORM SERVICE: not all forms are valid or additional submit validation failed", this);
					!!onValidationsFailedAction && onValidationsFailedAction();
				}

				!!finalizeAction && finalizeAction();
			})
	}

	private requestSubmitInAllFormServices(): Observable<boolean[]> {
		let initializedFormServices = this.allFormServices.filter(formService => formService.isFormInitialized());
		this.debug && console.info("COMPOSITE FORM SERVICE: Calling 'onSubmitRequestedByParentFormService()' on all initialized form services. Below you should see 'After fork join[...]'. If it's not there, " +
			"it means that one or more form services did not trigger form.onStatusChange event for status 'VALID' or 'INVALID'", initializedFormServices);
		let obs$ = forkJoin(initializedFormServices.map(formService => formService.onSubmitRequestedByParentFormService()))
			.pipe(tap(validity => this.debug && console.info("COMPOSITE FORM SERVICE: After fork join: received validity info for all initialized form services: ", validity)));
		if (environment.measureFormSubmitTime) {
			obs$.pipe(timeInterval(), map(val => val.interval)).subscribe(interval => console.info(`CompositeFormService.requestSubmitInAllFormServices; duration [ms] = ${interval}`))
		}
		return obs$;
	}

	getAllFormServices(): FormService[] {
		return this.allFormServices;
	}

	shouldShowMainValidationMessage(): boolean {
		return this.allFormServices.some(formService => formService.shouldShowMainValidationMessage());
	}

	copyAnswers(from: Product, to: Product) {
		this.allFormServices.filter(fs => fs.isFormInitialized()).forEach(formService => formService.copyAnswers(from.standard.name, to.standard.name));
	}

	enableInputs(): void {
		this.allFormServices.forEach(formService => formService.enableInputs());
	}

	destroy() {
		this.submitClicked.next(false);
		this.allFormServices.forEach(fs => fs.destroy());
	}

	protected onSubmitClicked$(): Observable<any> {
		return this.submitClicked.asObservable();
	}

	private blockInputs(): void {
		return this.getAllFormServices().forEach(fs => fs.blockInputs());
	}
}
